import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import { useLazySearchSalonSuggestionsQuery } from '../../../services/salonsApi';
import { theme } from '../../../theme';

const DEFAULT_VALUE = {
  query: '',
  categoryId: '',
  radiusKm: 'all',
  selectedPlace: null,
  coordinates: null,
  mode: 'text',
};

const normalizeRadius = (value) => {
  if (value === 'all' || value === '' || value === null) return 'all';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 'all';
};

const CONTROL_SECTION_SX = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  minHeight: 56,
  px: { xs: 1, md: 1.5 },
  backgroundColor: { xs: 'rgba(0, 0, 0, 0.02)', md: 'transparent' },
  borderRadius: { xs: '8px', md: 0 },
  borderRight: {
    xs: 'none',
    md: '1px solid rgba(0, 0, 0, 0.12)',
  },
  '& .MuiInputBase-root': {
    width: '100%',
    minHeight: 56,
  },
  '& .MuiInputBase-input, & .MuiSelect-select': {
    boxSizing: 'border-box',
    py: 1.5,
  },
};

const FIELD_SX = {
  width: '100%',
  '& .MuiInput-underline:before, & .MuiInput-underline:after': {
    display: 'none',
  },
};

const ACTION_BUTTON_BASE_SX = {
  minHeight: { xs: 48, md: 56 },
  width: { xs: '92%', md: 'auto' },
  mx: { xs: 'auto', md: 0 },
  px: 3,
  borderRadius: '10px',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.14)',
};

const SEARCH_BUTTON_SX = {
  ...ACTION_BUTTON_BASE_SX,
  color: '#fff',
  backgroundColor: theme.colors.primary[600],
  '&:hover': {
    backgroundColor: theme.colors.primary[700],
  },
  '&.Mui-disabled': {
    color: '#fff',
    backgroundColor: theme.colors.primary[600],
    opacity: 0.65,
  },
};

const NEARBY_BUTTON_SX = {
  ...ACTION_BUTTON_BASE_SX,
  color: theme.colors.primary[600],
  backgroundColor: '#fff',
  border: `1px solid ${theme.colors.primary[600]}`,
  '&:hover': {
    color: theme.colors.primary[700],
    backgroundColor: theme.colors.primary[50],
    borderColor: theme.colors.primary[700],
  },
  '&.Mui-disabled': {
    color: theme.colors.primary[600],
    backgroundColor: '#fff',
    opacity: 0.65,
  },
};

const UnifiedSalonSearchForm = ({
  categories = [],
  categoriesLoading = false,
  initialValue = DEFAULT_VALUE,
  onSearch,
  loading = false,
}) => {
  const { t, i18n } = useTranslation();
  const [triggerSuggestions, { isFetching }] = useLazySearchSalonSuggestionsQuery();
  const [query, setQuery] = useState(initialValue.query || '');
  const [categoryId, setCategoryId] = useState(initialValue.categoryId || '');
  const [radiusKm, setRadiusKm] = useState(
    normalizeRadius(initialValue.radiusKm),
  );
  const [selectedPlace, setSelectedPlace] = useState(
    initialValue.selectedPlace || null,
  );
  const [options, setOptions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [error, setError] = useState('');

  // Suggestions are optional assistance. A slow external place provider must
  // never prevent the user from launching the direct database search.
  const busy = loading || locationLoading;
  const language = (i18n.language || 'fr').split('-')[0];

  useEffect(() => {
    setQuery(initialValue.query || '');
    setCategoryId(initialValue.categoryId || '');
    setRadiusKm(normalizeRadius(initialValue.radiusKm));
    setSelectedPlace(initialValue.selectedPlace || null);
  }, [initialValue]);

  useEffect(() => {
    const trimmed = query.trim();
    if (
      trimmed.length < 2
      || selectedPlace?.label === trimmed
      || selectedPlace?.place_value === trimmed
    ) {
      if (trimmed.length < 2) {
        setOptions((current) => (current.length ? [] : current));
      }
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        const response = await triggerSuggestions({
          q: trimmed,
          language,
          countryBias: 'dz',
          limit: 8,
        }).unwrap();
        if (active) setOptions(response?.results || []);
      } catch (suggestionError) {
        if (active) setOptions([]);
      }
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [language, query, selectedPlace, triggerSuggestions]);

  const radiusOptions = useMemo(() => [
    { value: 'all', label: t('landing_search_salon.all_area') },
    { value: 5, label: '5 km' },
    { value: 10, label: '10 km' },
    { value: 25, label: '25 km' },
    { value: 50, label: '50 km' },
  ], [t]);

  const locateAndSearch = () => {
    if (!navigator.geolocation) {
      setError(t('landing_search_salon.location_permission_error'));
      return;
    }

    setLocationLoading(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const nextCoordinates = {
          latitude: Number(coords.latitude),
          longitude: Number(coords.longitude),
        };
        setSelectedPlace(null);
        setLocationLoading(false);
        onSearch({
          query: query.trim(),
          categoryId,
          radiusKm: radiusKm === 'all' ? null : radiusKm,
          selectedPlace: null,
          coordinates: nextCoordinates,
          mode: 'nearby',
        });
      },
      () => {
        setLocationLoading(false);
        setError(t('landing_search_salon.location_permission_error'));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  const submit = (event) => {
    event?.preventDefault();
    const trimmedQuery = query.trim();
    const placeCoordinates = selectedPlace
      ? {
        latitude: selectedPlace.latitude,
        longitude: selectedPlace.longitude,
      }
      : null;
    setError('');
    onSearch({
      query: trimmedQuery,
      categoryId,
      radiusKm: !trimmedQuery || radiusKm === 'all'
        ? 'all'
        : Number(radiusKm),
      selectedPlace,
      coordinates: placeCoordinates,
      mode: 'text',
    });
  };

  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'minmax(0, 1fr) auto auto',
        },
        gap: { xs: 1.5, md: 1 },
        width: '100%',
        alignItems: 'start',
      }}
    >
      <Paper
        data-testid="salon-search-controls"
        elevation={0}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(360px, 3fr) minmax(180px, 1fr) minmax(180px, 1fr)',
          },
          gap: { xs: 1, md: 0 },
          width: '100%',
          mx: 'auto',
          p: { xs: 2, md: '4px 8px' },
          borderRadius: '12px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box data-testid="salon-query-control" sx={CONTROL_SECTION_SX}>
          <Autocomplete
            data-testid="salon-query-autocomplete"
            fullWidth
            freeSolo
            sx={{
              width: '100%',
              '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                flex: '1 1 auto !important',
                width: 'auto !important',
                minWidth: 0,
              },
            }}
            filterOptions={(items) => items}
            options={options}
            inputValue={query}
            value={selectedPlace}
            loading={isFetching}
            disabled={loading}
            getOptionLabel={(option) => (
              typeof option === 'string' ? option : option?.label || ''
            )}
            isOptionEqualToValue={(option, selected) => (
              option.kind === selected.kind
              && option.label === selected.label
              && option.salon_id === selected.salon_id
            )}
            onInputChange={(event, value, reason) => {
              if (reason !== 'input' && reason !== 'clear') return;
              setQuery(value);
              setError('');
              if (selectedPlace) {
                setSelectedPlace(null);
                setRadiusKm('all');
              }
            }}
            onChange={(event, suggestion) => {
              if (!suggestion || typeof suggestion === 'string') {
                setSelectedPlace(null);
                return;
              }
              setQuery(suggestion.label);
              setOptions([]);
              setError('');
              if (suggestion.kind === 'place') {
                setSelectedPlace(suggestion);
              } else {
                setSelectedPlace(null);
                setRadiusKm('all');
              }
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box key={key} component="li" {...optionProps} sx={{ gap: 1 }}>
                  <Chip
                    size="small"
                    label={option.kind === 'salon'
                      ? t('landing_search_salon.suggestion_salon')
                      : t('landing_search_salon.suggestion_place')}
                    color={option.kind === 'salon' ? 'primary' : 'default'}
                  />
                  {option.label}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder={t('landing_search_salon.unified_placeholder')}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                  endAdornment: (
                    <>
                      {isFetching ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': t('landing_search_salon.unified_placeholder'),
                }}
                sx={FIELD_SX}
              />
            )}
          />
        </Box>

        <Box sx={CONTROL_SECTION_SX}>
          <TextField
            select
            variant="standard"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            disabled={categoriesLoading || loading}
            InputProps={{ disableUnderline: true }}
            SelectProps={{ displayEmpty: true }}
            inputProps={{
              'aria-label': t('landing_search_salon.all_categories'),
            }}
            sx={FIELD_SX}
          >
            <MenuItem value="">{t('landing_search_salon.all_categories')}</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ ...CONTROL_SECTION_SX, borderRight: 'none' }}>
          <TextField
            select
            variant="standard"
            value={radiusKm}
            onChange={(event) => setRadiusKm(normalizeRadius(event.target.value))}
            disabled={loading}
            InputProps={{ disableUnderline: true }}
            inputProps={{
              'aria-label': t('landing_search_salon.radius'),
            }}
            sx={FIELD_SX}
          >
            {radiusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      <Button
        type="submit"
        variant="contained"
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
        disabled={busy}
        sx={SEARCH_BUTTON_SX}
      >
        {t('landing_search_salon.search')}
      </Button>

      <Button
        type="button"
        variant="contained"
        startIcon={locationLoading
          ? <CircularProgress size={16} color="inherit" />
          : <MyLocationIcon />}
        onClick={locateAndSearch}
        disabled={busy}
        sx={NEARBY_BUTTON_SX}
      >
        {t('landing_search_salon.near_me')}
      </Button>

      {error && (
        <Alert severity="error" sx={{ gridColumn: '1 / -1' }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default UnifiedSalonSearchForm;
